import type { ReactNode } from 'react'
import { Box, Paper, TableCell, TableRow, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

// Gemeinsame Bausteine des Adminbereichs, damit alle Sections gleich aussehen:
// Seitenkopf (AdminPageHeader), Karten (AdminCard mit optionalem Formular-Footer
// AdminCardFooter) sowie einheitliche Tabellen (adminTableSx + AdminTableEmpty).

type AdminPageHeaderProps = {
  title: string
  description?: string
  // Primäraktionen der Seite (z. B. „Neuer Nutzer“), rechtsbündig.
  actions?: ReactNode
}

/** Kopf einer Admin-Seite: Titel, optionale Beschreibung, Aktionen rechts. */
export const AdminPageHeader = ({ title, description, actions }: AdminPageHeaderProps) => (
  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
    <Box>
      <Typography variant="h5" fontWeight={600}>
        {title}
      </Typography>
      {description !== undefined && (
        <Typography variant="body2" color="text.secondary" mt={0.5} maxWidth={640}>
          {description}
        </Typography>
      )}
    </Box>
    {actions !== undefined && (
      <Box display="flex" alignItems="center" gap={1} flexShrink={0} pt={0.5}>
        {actions}
      </Box>
    )}
  </Box>
)

type AdminCardProps = {
  title?: ReactNode
  description?: ReactNode
  // Sekundäre Bedienelemente im Kartenkopf (z. B. ein Filter-Switch).
  actions?: ReactNode
  // true für Freitext/Formulare; Tabellen und Listen rendern randlos.
  padded?: boolean
  children: ReactNode
  sx?: SxProps<Theme>
}

/** Einheitliche Karte des Adminbereichs mit optionalem Kopf (Titel/Aktionen). */
export const AdminCard = ({ title, description, actions, padded = false, children, sx }: AdminCardProps) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', ...sx }}>
    {(title !== undefined || actions !== undefined) && (
      <Box
        px={2}
        py={1.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        borderBottom={1}
        borderColor="divider"
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {description !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        {actions !== undefined && (
          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            {actions}
          </Box>
        )}
      </Box>
    )}
    {padded ? <Box p={2}>{children}</Box> : children}
  </Paper>
)

/** Abgetrennter Fußbereich einer AdminCard, z. B. für „Hinzufügen“-Formulare. */
export const AdminCardFooter = ({ children }: { children: ReactNode }) => (
  <Box px={2} py={2} borderTop={1} borderColor="divider">
    {children}
  </Box>
)

/** Tabellen-Styling in AdminCards: dezente Kopfzeile, letzte Zeile ohne Trennlinie. */
export const adminTableSx: SxProps<Theme> = {
  '& .MuiTableCell-head': {
    color: 'text.secondary',
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 'none',
  },
}

/** Platzhalterzeile für leere Admin-Tabellen. */
export const AdminTableEmpty = ({ colSpan, children }: { colSpan: number; children: ReactNode }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ border: 'none' }}>
      <Box py={5} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {children}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
)
